import { useCallback, useEffect, useRef } from 'react'

/**
 * Trailing-debounce wrapper around a callback. Returns a stable function
 * that delays calling `fn` until `delayMs` has elapsed since the last
 * invocation. The latest arguments win.
 *
 * Usage: SSE event bursts where the backend can emit 5-10 events in
 * <1s during ingestion; without a debounce, every event triggers a
 * full refetch fanout. 250-300ms is enough to coalesce a burst.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs: number
): (...args: TArgs) => void {
  const fnRef = useRef(fn)
  fnRef.current = fn

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  return useCallback(
    (...args: TArgs) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        fnRef.current(...args)
      }, delayMs)
    },
    [delayMs]
  )
}
